"use client";

/**
 * /install - the canonical explanation of how to install Mənzil.ai
 * as a PWA and why. Shows a platform-aware primary CTA (native prompt
 * on Chromium, "Add to Home Screen" instructions on iOS), and three
 * illustrated steps so users understand what they get.
 *
 * static fallback otherwise, so ship-readiness doesn't depend on it.
 */
import { useEffect, useState } from "react";
import SharedHeader from "@/components/SharedHeader";
import PhoneShareSheetIllustration from "@/components/PhoneShareSheetIllustration";
import { useT } from "@/lib/locale-context";
import { useInstallPrompt } from "@/lib/use-install-prompt";
import { ShareFlowAnim } from "@/components/animations/SceneAnimations";
import { TAG } from "@/lib/ui-classes";
import {
  Share as ShareIcon,
  Home as HomeIcon,
  Download,
  CheckCircle2,
  Zap,
} from "lucide-react";


// iCloud-hosted share URL for our Shortcuts-app Shortcut. iOS Safari
// doesn't support Web Share Target, so PWA install alone doesn't put
// Mənzil.ai into the OS share sheet. The Shortcut fills that gap -
// tapping this URL on an iPhone opens the Shortcuts app with an
// "Add Shortcut" dialog; once added, Mənzil.ai appears in Safari's
// share sheet for bina.az pages. Cross-iCloud-account friendly.
const IOS_SHORTCUT_URL =
  "https://www.icloud.com/shortcuts/9c721d088fd24b6aa327a3074ada817c";

type Platform = "android-chromium" | "ios" | "desktop" | "unknown";

function detectPlatform(): Platform {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  // iOS first - iPadOS 13+ reports itself as Mac, but has touch.
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
                (ua.includes("Mac") && "ontouchend" in document);
  if (isIOS) return "ios";
  if (/Android/.test(ua)) return "android-chromium";
  // Everything else (Chrome/Edge on desktop, Firefox, etc.) - we
  // still let them install but the instructions are less specific.
  return "desktop";
}

export default function InstallPage() {
  const t = useT();
  const { canInstall, installed, resolving, promptInstall } = useInstallPrompt();
  const [platform, setPlatform] = useState<Platform>("unknown");

  // Single install action used by both the hero CTA and the sticky
  // bottom bar. The button is always rendered so users never see a
  // dead-end hint - if the browser won't trigger a native prompt,
  // we scroll to the on-page step-by-step instead.
  const handleInstall = async () => {
    if (canInstall) {
      await promptInstall();
      return;
    }
    const el = document.getElementById("how-to-install");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Post-install handoff: hand the user a big "Open Mənzil.ai" CTA
  // after Chrome finishes installing. Navigating to the manifest's
  // start_url is the most reliable trigger we have - Android Chrome
  // routes that URL to the newly installed PWA via its scope match,
  // desktop Chrome opens the standalone window. There is no API to
  // launch a PWA directly from a tab; this is the best-effort handoff.
  const handleOpenApp = () => {
    window.location.href = "/";
  };

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <SharedHeader />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-12">
        {/* ── Hero ─────────────────────────────────────────── */}
        <section className="text-center">
          <div className="mx-auto h-40 w-40 md:h-48 md:w-48 flex items-center justify-center">
            {/* Same app icon as the rest of the install surfaces -
                consistent across the first-visit sheet, the post-
                analysis card, the corner banner and this hero. (The
                page used to ask for /lottie/install.json on every
                visit and 404; the file never existed.) */}
            <img
              src="/logo.svg"
              alt=""
              aria-hidden="true"
              className="h-32 w-32 rounded-[2rem] shadow-lg"
            />
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight">
            {t("install.hero.title")}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-gray-600 leading-relaxed max-w-md mx-auto">
            {t("install.hero.body")}
          </p>

          {/* Sticky CTA - pins under the SharedHeader when the user
              scrolls past the hero so the primary action stays one
              tap away without a separate fixed bottom bar (the bar
              was hidden by the MobileTabBar on small screens).
              top-16 clears the ~56 px header; the gray-50/95
              backdrop-blur matches the page bg so sticky state reads
              as floating rather than a gray block overlay. */}
          <div className="sticky top-16 z-20 mt-6 -mx-4 px-4 py-2 bg-gray-50/95 backdrop-blur">
            {installed ? (
              // "Already installed" treatment - a clear badge with the
              // device check + a secondary "Open app" link. A plain
              // green button wasn't enough of a signal; users landing
              // here from a bookmark couldn't tell whether they needed
              // to act or not.
              <div className="inline-flex flex-col items-center gap-2">
                <div className="inline-flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/40 px-4 py-3 text-sm font-semibold text-emerald-800 dark:text-emerald-200">
                  <CheckCircle2 className="h-5 w-5" />
                  {t("install.hero.alreadyinstalled")}
                </div>
                <button
                  type="button"
                  onClick={handleOpenApp}
                  className="text-xs font-medium text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900"
                >
                  {t("install.hero.openapp")} →
                </button>
              </div>
            ) : resolving ? (
              // Skeleton while we wait on beforeinstallprompt or the
              // grace timer. Holds the button's place so the label
              // doesn't flip in front of the user.
              <div className="inline-flex h-11 w-44 items-center justify-center rounded-xl bg-gray-100 animate-pulse" />
            ) : platform === "ios" ? (
              // iOS: Safari never supports Web Share Target, so the
              // native install prompt doesn't help. The iCloud Shortcut
              // is the real value proposition here - surface it as the
              // primary CTA instead of the PWA steps.
              <a
                href={IOS_SHORTCUT_URL}
                className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
              >
                <Download className="h-4 w-4" />
                {t("install.ios.shortcut.cta")}
              </a>
            ) : (
              <button
                type="button"
                onClick={handleInstall}
                className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
              >
                <Download className="h-4 w-4" />
                {t(canInstall ? "install.hero.cta" : "install.hero.showsteps")}
              </button>
            )}
          </div>
        </section>

        {/* ── iOS-only: Add our Shortcut ─────────────────────
            iOS Safari doesn't implement Web Share Target, so a vanilla
            PWA install doesn't put Mənzil.ai into bina.az's share
            menu on iPhone. Our Shortcuts-app Shortcut fills that gap:
            one tap adds it to the user's iOS Shortcuts library, then
            it appears in Safari's share sheet like any other share
            target. Shown only on iOS - on Android and desktop this
            section is silent. */}
        {platform === "ios" && (
          <section className="mt-10">
            {/* Explainer-only card on iOS - the actual CTA already
                lives in the hero above and the sticky bar below, so
                repeating it here was three buttons for one action. */}
            <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100">
                  <ShareIcon className="h-5 w-5 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                    {t("install.ios.shortcut.title")}
                  </h2>
                  <p className="mt-1 text-sm text-gray-700">
                    {t("install.ios.shortcut.body")}
                  </p>
                  <p className="mt-2 text-[11px] text-gray-500">
                    {t("install.ios.shortcut.note")}
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ── Why install ──────────────────────────────────── */}
        <section className="mt-12">
          {/* The habit loop, drawn: a bina.az card slides in, shares
              into the app squircle, the verdict chip pops out. Words
              below explain it; this shows it. */}
          <div className="mx-auto mb-4 h-28 w-full max-w-[220px]">
            <ShareFlowAnim />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 text-center">
            {t("install.why.title")}
          </h2>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <BenefitCard
              icon={<HomeIcon className="h-5 w-5" />}
              title={t("install.why.home.title")}
              body={t("install.why.home.body")}
            />
            <BenefitCard
              icon={<ShareIcon className="h-5 w-5" />}
              title={t("install.why.share.title")}
              body={t("install.why.share.body")}
            />
            <BenefitCard
              icon={<Zap className="h-5 w-5" />}
              title={t("install.why.fast.title")}
              body={t("install.why.fast.body")}
            />
          </div>
        </section>

        {/* ── How - platform-aware steps ────────────────────── */}
        <section id="how-to-install" className="mt-12 scroll-mt-24">
          <h2 className="text-xl font-semibold text-gray-900 text-center">
            {t("install.how.title")}
          </h2>

          {platform === "ios" ? (
            <IOSSteps t={t} />
          ) : (
            <AndroidSteps t={t} />
          )}
        </section>

        {/* ── Share-target teaser - the killer feature users
            don't know about until they see it ───────────── */}
        <section className="mt-12">
          <h2 className="text-xl font-semibold text-gray-900 text-center">
            {t("install.sharetarget.title")}
          </h2>
          <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-6">
            <PhoneShareSheetIllustration />
            <p className="mt-4 text-sm text-gray-600 text-center">
              {t("install.sharetarget.body")}
            </p>
          </div>
        </section>
      </main>

    </div>
  );
}

// ── Internal pieces ───────────────────────────────────────

// globals.css swaps Tailwind gray vars in dark mode (text-gray-900
// auto-becomes near-white, bg-white auto-becomes dark) so plain
// utilities work in both themes - don't add dark: overrides for
// these or they'll fight the swap and render the WRONG shade.
function BenefitCard({
  icon, title, body,
}: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center text-gray-500">
          {icon}
        </div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      </div>
      <p className="mt-2 text-xs text-gray-600">{body}</p>
    </div>
  );
}

function Step({ num, title, body, icon }: {
  num: number; title: string; body: string; icon: React.ReactNode;
}) {
  return (
    <div className="flex gap-4 rounded-2xl border border-gray-200 bg-white p-4">
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${TAG}`}>
        {num}
      </div>
      <div className="flex-1">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          {icon} {title}
        </h3>
        <p className="mt-1 text-sm text-gray-600">{body}</p>
      </div>
    </div>
  );
}

function AndroidSteps({ t }: {
  t: (k: Parameters<ReturnType<typeof useT>>[0]) => string;
}) {
  return (
    <div className="mt-4 space-y-3">
      <Step
        num={1}
        icon={<Download className="h-4 w-4 text-gray-400" />}
        title={t("install.android.step1.title")}
        body={t("install.android.step1.body")}
      />
      <Step
        num={2}
        icon={<HomeIcon className="h-4 w-4 text-gray-400" />}
        title={t("install.android.step2.title")}
        body={t("install.android.step2.body")}
      />
      <Step
        num={3}
        icon={<ShareIcon className="h-4 w-4 text-gray-400" />}
        title={t("install.android.step3.title")}
        body={t("install.android.step3.body")}
      />
    </div>
  );
}

function IOSSteps({ t }: {
  t: (k: Parameters<ReturnType<typeof useT>>[0]) => string;
}) {
  // Step icons match the new Shortcut-install flow: Download → Check
  // (confirm in Shortcuts) → Share (use from Safari). Not the old
  // PWA-flavoured Share → Link → Home.
  return (
    <div className="mt-4 space-y-3">
      <Step
        num={1}
        icon={<Download className="h-4 w-4 text-gray-400" />}
        title={t("install.ios.step1.title")}
        body={t("install.ios.step1.body")}
      />
      <Step
        num={2}
        icon={<CheckCircle2 className="h-4 w-4 text-gray-400" />}
        title={t("install.ios.step2.title")}
        body={t("install.ios.step2.body")}
      />
      <Step
        num={3}
        icon={<ShareIcon className="h-4 w-4 text-gray-400" />}
        title={t("install.ios.step3.title")}
        body={t("install.ios.step3.body")}
      />
    </div>
  );
}

